import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type UnsubscribeStatus = "loading" | "success" | "error" | "invalid";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<UnsubscribeStatus>("loading");
  const [maskedEmail, setMaskedEmail] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setStatus("invalid");
      setErrorMessage("No unsubscribe token provided");
      return;
    }

    const unsubscribe = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/unsubscribe-alerts?token=${token}`,
          { method: "GET" }
        );

        const data = await response.json();

        if (!response.ok) {
          setStatus("error");
          setErrorMessage(data.error || "Failed to unsubscribe");
          return;
        }

        setStatus("success");
        setMaskedEmail(data.email || "");
      } catch (error) {
        console.error("Unsubscribe error:", error);
        setStatus("error");
        setErrorMessage("An unexpected error occurred");
      }
    };

    unsubscribe();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === "loading" && (
            <>
              <div className="mx-auto mb-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
              </div>
              <CardTitle>Processing...</CardTitle>
              <CardDescription>Please wait while we process your request</CardDescription>
            </>
          )}

          {status === "success" && (
            <>
              <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <CardTitle>Successfully Unsubscribed</CardTitle>
              <CardDescription>
                {maskedEmail ? (
                  <>You ({maskedEmail}) have been removed from our mailing list.</>
                ) : (
                  <>You have been removed from our mailing list.</>
                )}
              </CardDescription>
            </>
          )}

          {(status === "error" || status === "invalid") && (
            <>
              <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle>Unsubscribe Failed</CardTitle>
              <CardDescription>{errorMessage}</CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent className="text-center">
          {status === "success" && (
            <p className="text-sm text-muted-foreground mb-6">
              You will no longer receive event alerts or notifications from us.
              If you change your mind, you can always subscribe again.
            </p>
          )}

          {(status === "error" || status === "invalid") && (
            <p className="text-sm text-muted-foreground mb-6">
              The unsubscribe link may be invalid or expired. 
              If you continue to receive emails, please contact support.
            </p>
          )}

          <Button asChild className="gap-2">
            <Link to="/">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Unsubscribe;
